import { SpotlightItem, SpotlightItemType } from '@thoughtspot/global-nav';
import { IconID } from '@thoughtspot/radiant-react/widgets/icon';
import { useCallback, useMemo } from 'react';
import {
    GetMetadataListQuery,
    ListType,
    SubTypes,
    useGetMetadataListLazyQuery as _useGetMetadataListLazyQuery,
} from '../../services/generated/graphql-types';

type MetadataObject = NonNullable<
    NonNullable<GetMetadataListQuery['getMetadataList']>['objects']
>[number];

/**
 * Lazily fetches the list of tables (ONE_TO_ONE_LOGICAL) visible to the user,
 * matching what the Data page shows under Tables. Triggered by calling activate().
 *
 * Used when the user selects the "Tables" filter in the command palette.
 */
export function useSpotlightTableItems() {
    const [fetchTables, { data, loading }] = _useGetMetadataListLazyQuery();

    const activate = useCallback(() => {
        fetchTables({
            fetchPolicy: 'network-only',
            variables: {
                params: {
                    type: ListType.LogicalTable,
                    subtypes: [SubTypes.OneToOneLogical],
                    batchSize: 100,
                    offset: 0,
                    sortAscending: false,
                    sort: 'MODIFIED',
                    showHidden: false,
                },
            },
        });
    }, [fetchTables]);

    const tableItems = useMemo<SpotlightItem[]>(() => {
        const objects = data?.getMetadataList?.objects;
        if (!objects) return [];
        return objects
            .filter(
                (obj: MetadataObject): obj is NonNullable<MetadataObject> =>
                    !!obj?.id && !!obj?.name,
            )
            .map((obj: NonNullable<MetadataObject>) => ({
                id: `table-spotlight-${obj.id}`,
                label: obj.name,
                description:
                    obj.authorDisplayName || obj.authorName
                        ? `by ${obj.authorDisplayName ?? obj.authorName}`
                        : undefined,
                icon: IconID.CHART_TABLE,
                type: SpotlightItemType.NAVIGATION,
                path: `/data/tables/${obj.id}`,
                keywords: ['table', 'data', 'source'],
            }));
    }, [data]);

    return { tableItems, tableItemsLoading: loading, activate };
}
